// Centro de operaciones del dashboard (admin/soporte): cola de soporte, SLA en
// vivo, tickets que requieren atencion, tendencia, carga del equipo, calidad y
// pendientes administrativos. Se refresca solo cada 60 s.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    AlertTriangle, Clock, Inbox, UserX, Hourglass, GitBranch, TrendingUp, TrendingDown, Minus,
    FileText, HardDrive, RefreshCw, ThumbsUp, Timer, CheckCircle2, Target, ClipboardCheck, UserCheck
} from 'lucide-react';
import {
    ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';
import api from '../services/api';

const COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];
const PRIO = {
    'Crítica': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    'Alta': 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
    'Media': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    'Baja': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
};

const TONOS = {
    neutral: 'border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white',
    alerta: 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300',
    aviso: 'border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300',
    ok: 'border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300',
};

function Panel({ title, icon: Icon, action, children, className = '' }) {
    return (
        <div className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm ${className}`}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-gray-700">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-2">
                    {Icon && <Icon className="w-4 h-4 text-blue-500" />}
                    {title}
                </h3>
                {action}
            </div>
            <div className="p-5">{children}</div>
        </div>
    );
}

function ColaTile({ label, value, icon: Icon, tono = 'neutral', sub, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`text-left rounded-xl border bg-white dark:bg-gray-800 p-4 transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${TONOS[tono]}`}
        >
            <div className="flex items-center justify-between">
                <span className="text-xs font-medium uppercase tracking-wider opacity-80">{label}</span>
                <Icon className="w-4 h-4 opacity-70" />
            </div>
            <p className="text-3xl font-bold mt-2 tabular-nums">{value}</p>
            {sub && <p className="text-xs mt-1 opacity-75">{sub}</p>}
        </button>
    );
}

function Delta({ actual, anterior }) {
    const d = actual - anterior;
    if (d === 0) return <span className="inline-flex items-center gap-0.5 text-xs text-gray-400"><Minus className="w-3 h-3" /> igual que ayer</span>;
    const sube = d > 0;
    const Icon = sube ? TrendingUp : TrendingDown;
    return (
        <span className={`inline-flex items-center gap-0.5 text-xs ${sube ? 'text-emerald-600' : 'text-gray-500'}`}>
            <Icon className="w-3 h-3" /> {sube ? '+' : ''}{d} vs ayer ({anterior})
        </span>
    );
}

function Metrica({ label, value, unit, icon: Icon, tono = 'neutral' }) {
    return (
        <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg border ${TONOS[tono]}`}><Icon className="w-4 h-4" /></div>
            <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
                <p className="text-lg font-bold text-gray-900 dark:text-white tabular-nums">
                    {value == null ? '—' : value}{value != null && unit ? <span className="text-xs font-normal text-gray-400 ml-1">{unit}</span> : null}
                </p>
            </div>
        </div>
    );
}

const tonoPct = (v) => (v == null ? 'neutral' : v >= 90 ? 'ok' : v >= 70 ? 'aviso' : 'alerta');

export default function DashboardOperativo() {
    const navigate = useNavigate();
    const [d, setD] = useState(null);
    const [error, setError] = useState(false);
    const [cargando, setCargando] = useState(false);

    const cargar = useCallback(async () => {
        setCargando(true);
        try {
            const { data } = await api.get('/dashboard/operativo');
            setD(data);
            setError(false);
        } catch {
            setError(true);
        } finally {
            setCargando(false);
        }
    }, []);

    useEffect(() => {
        cargar();
        const t = setInterval(cargar, 60000);
        return () => clearInterval(t);
    }, [cargar]);

    if (!d) {
        return (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 text-sm text-gray-500">
                {error ? 'No se pudo cargar el centro de operaciones.' : 'Cargando centro de operaciones…'}
            </div>
        );
    }

    const { cola, hoy, atencion, carga, tendencia, calidad, porCategoria, administrativo } = d;
    const maxCarga = Math.max(1, ...carga.map((c) => c.total));
    const maxCat = Math.max(1, ...porCategoria.map((c) => c.total));
    const tend = tendencia.map((x) => ({ ...x, dia: x.fecha.slice(8, 10) + '/' + x.fecha.slice(5, 7) }));
    const adm = administrativo;

    return (
        <section className="space-y-6" aria-label="Centro de operaciones">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Centro de operaciones</h2>
                <button
                    type="button"
                    onClick={cargar}
                    disabled={cargando}
                    className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white"
                    title="Actualizar ahora"
                >
                    <RefreshCw className={`w-3.5 h-3.5 ${cargando ? 'animate-spin' : ''}`} />
                    Actualizado {new Date(d.generadoEn).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}
                </button>
            </div>

            {/* Cola de soporte */}
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
                <ColaTile label="Sin asignar" value={cola.sinAsignar} icon={UserX} tono={cola.sinAsignar > 0 ? 'aviso' : 'neutral'} sub="esperan un técnico" onClick={() => navigate('/tickets?tab=unassigned')} />
                <ColaTile label="En proceso" value={cola.enProceso} icon={Clock} sub={`${cola.abiertos} abiertos en total`} onClick={() => navigate('/tickets')} />
                <ColaTile label="Esperando usuario" value={cola.esperandoUsuario} icon={Hourglass} sub="SLA en pausa" onClick={() => navigate('/tickets?tab=waiting')} />
                <ColaTile label="Fuera de SLA" value={cola.vencidos} icon={AlertTriangle} tono={cola.vencidos > 0 ? 'alerta' : 'ok'} sub={cola.vencidos > 0 ? 'requieren acción ya' : 'todo dentro del límite'} onClick={() => navigate('/tickets')} />
                <ColaTile label="Por vencer" value={cola.porVencer} icon={Timer} tono={cola.porVencer > 0 ? 'aviso' : 'neutral'} sub="sobre el 80% del límite" onClick={() => navigate('/tickets')} />
                <ColaTile label="Derivaciones" value={cola.derivaciones} icon={GitBranch} tono={cola.derivaciones > 0 ? 'aviso' : 'neutral'} sub="pendientes de respuesta" onClick={() => navigate('/tickets')} />
            </div>

            {/* Hoy + Requieren atencion */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Panel title="Hoy" icon={Inbox}>
                    <div className="space-y-5">
                        <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Tickets recibidos</p>
                            <p className="text-3xl font-bold text-gray-900 dark:text-white tabular-nums">{hoy.creadosHoy}</p>
                            <Delta actual={hoy.creadosHoy} anterior={hoy.creadosAyer} />
                        </div>
                        <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Tickets resueltos</p>
                            <p className="text-3xl font-bold text-gray-900 dark:text-white tabular-nums">{hoy.resueltosHoy}</p>
                            <Delta actual={hoy.resueltosHoy} anterior={hoy.resueltosAyer} />
                        </div>
                        <div className="pt-4 border-t border-gray-100 dark:border-gray-700 grid grid-cols-2 gap-3">
                            <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Mis abiertos</p>
                                <p className="text-xl font-bold text-gray-900 dark:text-white tabular-nums">{hoy.misAbiertos}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Resueltos por mí hoy</p>
                                <p className="text-xl font-bold text-gray-900 dark:text-white tabular-nums">{hoy.misResueltosHoy}</p>
                            </div>
                        </div>
                    </div>
                </Panel>

                <Panel
                    title="Requieren atención"
                    icon={AlertTriangle}
                    className="lg:col-span-2"
                    action={d.atencionTotal > atencion.length ? <span className="text-xs text-gray-400">{atencion.length} de {d.atencionTotal}</span> : null}
                >
                    {atencion.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8 text-center">
                            <CheckCircle2 className="w-10 h-10 text-emerald-500 mb-2" />
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Nada urgente por ahora</p>
                            <p className="text-xs text-gray-400 mt-1">Sin tickets vencidos, sin asignar ni esperando demasiado.</p>
                        </div>
                    ) : (
                        <ul className="divide-y divide-gray-100 dark:divide-gray-700 -my-2">
                            {atencion.map((t) => (
                                <li key={t.id}>
                                    <button
                                        type="button"
                                        onClick={() => navigate(`/tickets/${t.id}`)}
                                        className="w-full text-left py-3 flex items-start gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/40 -mx-2 px-2 rounded-lg"
                                    >
                                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 pt-0.5 w-10 shrink-0">#{t.id}</span>
                                        <span className="min-w-0 flex-1">
                                            <span className="block text-sm font-medium text-gray-900 dark:text-white truncate">{t.descripcion || 'Sin descripción'}</span>
                                            <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">
                                                {t.solicitante}{t.categoria ? ` · ${t.categoria}` : ''} · {t.asignadoA ? `Asignado a ${t.asignadoA}` : 'Sin asignar'}
                                            </span>
                                            <span className="block text-xs font-medium text-red-600 dark:text-red-400 mt-0.5">{t.motivo}</span>
                                        </span>
                                        <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${PRIO[t.prioridad] || PRIO.Media}`}>{t.prioridad}</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </Panel>
            </div>

            {/* Tendencia + carga */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Panel title="Tickets últimos 14 días" icon={TrendingUp} className="lg:col-span-2">
                    <div style={{ width: '100%', height: 240 }}>
                        <ResponsiveContainer>
                            <AreaChart data={tend}>
                                <defs>
                                    <linearGradient id="opCreados" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="opResueltos" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis dataKey="dia" tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                <Tooltip />
                                <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                                <Area type="monotone" dataKey="creados" name="Recibidos" stroke="#8b5cf6" fill="url(#opCreados)" strokeWidth={2} />
                                <Area type="monotone" dataKey="resueltos" name="Resueltos" stroke="#22c55e" fill="url(#opResueltos)" strokeWidth={2} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </Panel>

                <Panel title="Carga del equipo" icon={UserCheck}>
                    {carga.length === 0 ? (
                        <p className="text-sm text-gray-400 text-center py-8">No hay tickets abiertos</p>
                    ) : (
                        <div className="space-y-3">
                            {carga.map((c, i) => (
                                <div key={c.nombre}>
                                    <div className="flex items-center justify-between text-sm mb-1">
                                        <span className={`truncate pr-2 ${c.nombre === 'Sin asignar' ? 'text-amber-600 font-medium' : 'text-gray-600 dark:text-gray-300'}`}>{c.nombre}</span>
                                        <span className="font-bold text-gray-900 dark:text-white tabular-nums">{c.total}</span>
                                    </div>
                                    <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2">
                                        <div className="h-2 rounded-full" style={{ width: `${(c.total / maxCarga) * 100}%`, background: c.nombre === 'Sin asignar' ? '#f59e0b' : COLORS[i % COLORS.length] }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Panel>
            </div>

            {/* Calidad 30 dias + categorias */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Panel title="Calidad del servicio (30 días)" icon={Target} className="lg:col-span-2">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                        <Metrica label="Resueltos" value={calidad.resueltos30} icon={CheckCircle2} />
                        <Metrica label="Satisfacción (CSAT)" value={calidad.csat} unit={`★ · ${calidad.csatRespuestas} reseñas`} icon={ThumbsUp} tono={calidad.csat == null ? 'neutral' : calidad.csat >= 4.5 ? 'ok' : calidad.csat >= 3.5 ? 'aviso' : 'alerta'} />
                        <Metrica label="SLA de resolución" value={calidad.slaResolucionPct} unit="%" icon={Target} tono={tonoPct(calidad.slaResolucionPct)} />
                        <Metrica label="Tiempo medio de resolución" value={calidad.resolucionHoras} unit="h" icon={Clock} />
                        <Metrica label="Primera respuesta" value={calidad.primeraRespuestaMin} unit="min" icon={Timer} />
                        <Metrica label="SLA de respuesta" value={calidad.slaRespuestaPct} unit="%" icon={Target} tono={tonoPct(calidad.slaRespuestaPct)} />
                    </div>
                    <p className="text-xs text-gray-400 mt-4">El detalle por agente y prioridad está en Reportes.</p>
                </Panel>

                <Panel title="Qué piden más (30 días)" icon={FileText}>
                    {porCategoria.length === 0 ? (
                        <p className="text-sm text-gray-400 text-center py-8">Sin tickets en el período</p>
                    ) : (
                        <div className="space-y-3">
                            {porCategoria.map((c, i) => (
                                <div key={c.categoria}>
                                    <div className="flex items-center justify-between text-sm mb-1">
                                        <span className="text-gray-600 dark:text-gray-300 truncate pr-2">{c.categoria}</span>
                                        <span className="font-bold text-gray-900 dark:text-white tabular-nums">{c.total}</span>
                                    </div>
                                    <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2">
                                        <div className="h-2 rounded-full" style={{ width: `${(c.total / maxCat) * 100}%`, background: COLORS[i % COLORS.length] }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Panel>
            </div>

            {/* Pendientes administrativos */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Panel title="Actas" icon={FileText}>
                    <ul className="space-y-2 text-sm">
                        {[
                            { label: 'Pendientes de firma del usuario', v: adm.actas.pendientes, to: '/gestion-actas?estado=Pendiente' },
                            { label: 'Subidas, por revisar', v: adm.actas.porRevisar, to: '/gestion-actas?estado=SubidaPorUsuario', tono: 'aviso' },
                            { label: 'Rechazadas', v: adm.actas.rechazadas, to: '/gestion-actas?estado=Rechazada', tono: 'alerta' },
                        ].map((x) => (
                            <li key={x.label}>
                                <button type="button" onClick={() => navigate(x.to)} className="w-full flex items-center justify-between py-1.5 px-2 -mx-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40">
                                    <span className="text-gray-600 dark:text-gray-300">{x.label}</span>
                                    <span className={`min-w-[1.75rem] text-center px-2 py-0.5 rounded-full text-xs font-bold ${x.v > 0 && x.tono === 'alerta' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' : x.v > 0 && x.tono === 'aviso' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>{x.v}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </Panel>

                <Panel title="Paz y Salvo en firma" icon={ClipboardCheck} action={<span className="text-xs font-bold text-gray-500">{adm.pazYSalvo.enFirma}</span>}>
                    {adm.pazYSalvo.lista.length === 0 ? (
                        <p className="text-sm text-gray-400 text-center py-4">No hay procesos esperando firma</p>
                    ) : (
                        <ul className="divide-y divide-gray-100 dark:divide-gray-700 -my-2">
                            {adm.pazYSalvo.lista.map((p) => (
                                <li key={p.id}>
                                    <button type="button" onClick={() => navigate(`/pazysalvo/${p.id}`)} className="w-full text-left py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/40 -mx-2 px-2 rounded-lg">
                                        <span className="block text-sm font-medium text-gray-900 dark:text-white truncate">{p.usuarioNombre}</span>
                                        <span className="block text-xs text-gray-500 dark:text-gray-400">
                                            {p.usuarioEmpresa ? `${p.usuarioEmpresa} · ` : ''}salida {new Date(p.fechaSalida).toLocaleDateString('es-CL')}
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </Panel>

                <Panel title="Inventario de la semana" icon={HardDrive}>
                    <div className="grid grid-cols-2 gap-4">
                        {[
                            { label: 'Disponibles', v: adm.activos.disponibles, to: '/activos' },
                            { label: 'Dados de baja', v: adm.activos.dadosDeBaja, to: '/activos' },
                            { label: 'Asignados (7 días)', v: adm.activos.asignacionesSemana, to: '/activos' },
                            { label: 'Devueltos (7 días)', v: adm.activos.devolucionesSemana, to: '/activos' },
                        ].map((x) => (
                            <button key={x.label} type="button" onClick={() => navigate(x.to)} className="text-left rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/40 p-2 -m-2">
                                <p className="text-xs text-gray-500 dark:text-gray-400">{x.label}</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">{x.v}</p>
                            </button>
                        ))}
                    </div>
                </Panel>
            </div>
        </section>
    );
}
